import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { isRapidPlan } from "../config/planAccess";
import { useDateRange, type DateRange } from "@/hooks/useDateRange";
import {
  clampAnalyticsDateRangeForTab,
  getAnalyticsDateRuleTab,
  getAnalyticsTabMaxDate,
  getDefaultAnalyticsDateRange,
  parseLocalDateParam,
  toAnalyticsDateParam,
} from "../utils/analyticsDateUtils";

type AnalyticsPageContextValue = {
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  resetDateRange: () => void;
  /** Upper bound for pickers; undefined means future dates are allowed. */
  maxSelectableDate: Date | undefined;
};

const AnalyticsPageContext = createContext<AnalyticsPageContextValue | null>(
  null,
);

const ANALYTICS_FROM_PARAM = "analyticsFrom";
const ANALYTICS_TO_PARAM = "analyticsTo";

function parseDateParam(value: string | null): Date | undefined {
  if (!value) {
    return undefined;
  }

  return parseLocalDateParam(value);
}

function serializeDateParam(value: Date | undefined): string | null {
  if (!value || Number.isNaN(value.getTime())) {
    return null;
  }

  return toAnalyticsDateParam(value) ?? null;
}

function datesEqual(a: Date | undefined, b: Date | undefined): boolean {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }

  return a.getTime() === b.getTime();
}

export function AnalyticsPageProvider({
  children,
  allowFutureDates = true,
}: {
  children: React.ReactNode;
  /** false for the multi-hotel admin dashboard: dates stay capped at today. */
  allowFutureDates?: boolean;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { userProfile } = useAuthenticatedUser();
  const rapid = isRapidPlan(userProfile);
  const {
    dateRange,
    setDateRange: setDateRangeState,
    resetDateRange: resetDateRangeState,
  } = useDateRange();

  const persistRangeToSearchParams = useCallback(
    (range: DateRange) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        const fromParam = serializeDateParam(range.from);
        const toParam = serializeDateParam(range.to);

        if (fromParam) {
          next.set(ANALYTICS_FROM_PARAM, fromParam);
        } else {
          next.delete(ANALYTICS_FROM_PARAM);
        }

        if (toParam) {
          next.set(ANALYTICS_TO_PARAM, toParam);
        } else {
          next.delete(ANALYTICS_TO_PARAM);
        }

        return next;
      });
    },
    [setSearchParams],
  );

  const searchTab = searchParams.get("tab");
  const activeTab = getAnalyticsDateRuleTab({
    searchTab,
    pathname,
    isRapidPlan: rapid,
    allowFutureDates,
  });

  const setDateRange = useCallback(
    (range: DateRange) => {
      const clamped = clampAnalyticsDateRangeForTab(range, activeTab);
      setDateRangeState(clamped);
      persistRangeToSearchParams(clamped);
    },
    [activeTab, persistRangeToSearchParams, setDateRangeState],
  );

  const resetDateRange = useCallback(() => {
    resetDateRangeState();
    persistRangeToSearchParams({ from: undefined, to: undefined });
  }, [persistRangeToSearchParams, resetDateRangeState]);

  useEffect(() => {
    const fromParam = searchParams.get(ANALYTICS_FROM_PARAM);
    const toParam = searchParams.get(ANALYTICS_TO_PARAM);

    if (!fromParam && !toParam) {
      if (!dateRange.from && !dateRange.to) {
        setDateRange(getDefaultAnalyticsDateRange());
      }
      return;
    }

    const parsed = clampAnalyticsDateRangeForTab(
      {
        from: parseDateParam(fromParam),
        to: parseDateParam(toParam),
      },
      activeTab,
    );

    const parsedFromParam = toAnalyticsDateParam(parsed.from);
    const parsedToParam = toAnalyticsDateParam(parsed.to);
    const needsUrlCorrection =
      parsedFromParam !== fromParam || parsedToParam !== toParam;

    if (
      !needsUrlCorrection &&
      datesEqual(dateRange.from, parsed.from) &&
      datesEqual(dateRange.to, parsed.to)
    ) {
      return;
    }

    if (needsUrlCorrection) {
      setDateRange(parsed);
      return;
    }

    setDateRangeState(parsed);
  }, [
    activeTab,
    dateRange.from,
    dateRange.to,
    searchParams,
    setDateRange,
    setDateRangeState,
  ]);

  const value = useMemo(
    () => ({
      dateRange,
      setDateRange,
      resetDateRange,
      maxSelectableDate: getAnalyticsTabMaxDate(activeTab),
    }),
    [dateRange, setDateRange, resetDateRange, activeTab],
  );

  return (
    <AnalyticsPageContext.Provider value={value}>
      {children}
    </AnalyticsPageContext.Provider>
  );
}

export function useAnalyticsPage(): AnalyticsPageContextValue {
  const context = useContext(AnalyticsPageContext);

  if (!context) {
    throw new Error(
      "useAnalyticsPage must be used within AnalyticsPageProvider",
    );
  }

  return context;
}
